import { useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

// This component will handle scroll restoration when navigating between routes
const ScrollToTop = ({ children }) => {
  const { pathname, hash } = useLocation();
  const prevPathname = useRef(pathname);

  // Using useLayoutEffect for immediate synchronous execution before browser paint
  useLayoutEffect(() => {
    // Skip if the path hasn't changed
    if (pathname === prevPathname.current) {
      return;
    }
    
    // Skip if there's a hash in the URL (e.g., for anchor links)
    if (hash) {
      return;
    }
    
    // Skip scroll restoration only when navigating TO the Quran page
    // which has its own scroll management
    if (pathname.includes('/quran')) {
      console.log('ScrollToTop: Skipping for Quran page');
      prevPathname.current = pathname;
      return;
    }

    // For all other navigation, including FROM Quran to other pages,
    // we want to reset the scroll position
    console.log('ScrollToTop: Executing for navigation from', prevPathname.current, 'to', pathname);
    
    // Store current pathname for the next comparison
    prevPathname.current = pathname;

    // Immediately reset window scroll
    resetAllScrollPositions();

    // Additional reset after a short delay to handle post-render scrolling
    const timeoutId = setTimeout(() => {
      resetAllScrollPositions();
    }, 50); // Increased timeout to 50ms
    
    // Additional reset after main content should be fully rendered
    const longTimeoutId = setTimeout(() => {
      resetAllScrollPositions();
    }, 250); // Longer timeout as a fallback
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(longTimeoutId);
    };
  }, [pathname, hash]);

  // Helper function to aggressively reset all possible scroll containers
  const resetAllScrollPositions = () => {
    // 1. Reset window scroll (most common case)
    window.scrollTo(0, 0);
    
    // 2. Reset document element and body (for browsers that use these)
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // 3. Target the html element directly
    const htmlElement = document.documentElement;
    if (htmlElement) {
      htmlElement.scrollTop = 0;
    }
    
    // 4. Find and reset main element
    const main = document.querySelector('main');
    if (main) {
      main.scrollTop = 0;
      
      // 5. Reset direct children of main that might be scrollable
      Array.from(main.children).forEach(child => {
        child.scrollTop = 0;
        
        // 6. Also try second-level children
        if (child.children) {
          Array.from(child.children).forEach(grandchild => {
            grandchild.scrollTop = 0;
          });
        }
      });
    }
    
    // 7. Target specific known containers in your app
    const containers = [
      document.querySelector('.min-h-screen'),
      document.querySelector('.container'),
      document.querySelector('.relative.z-10'),
      // Add other potential container selectors here
    ];
    
    containers.forEach(container => {
      if (container) {
        container.scrollTop = 0;
      }
    });
    
    // 8. Find all elements with overflow:auto or overflow:scroll
    const scrollableElements = Array.from(document.querySelectorAll('*')).filter(el => {
      const style = window.getComputedStyle(el);
      return (
        (style.overflowY === 'auto' || 
         style.overflowY === 'scroll' || 
         style.overflowY === 'overlay')
      );
    });
    
    scrollableElements.forEach(el => {
      el.scrollTop = 0;
    });
    
    // Log what we're doing to help with debugging
    console.log('ScrollToTop: Reset scroll positions for path:', pathname);
  };

  return children;
};

export default ScrollToTop; 